import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLatest } from 'react-use';
import { CURRENT_PRODUCT_MODE } from '@utils/constants';
import { getUserProfile } from '@store/common/selectors';
import apptracker from '@utils/sensor/apptracker';
import {
  OPEN_FILTER_MANAGEMENT_PANEL,
  CREATE_FILTER_ON_FILTER_MANAGEMENT,
  DELETE_FILTER_ON_FILTER_MANAGEMENT,
} from '@src/utils/sensor/track';

import { Management, DataSource, type ManagementProps, type User } from '../components';
import {
  useFilterMeta,
  useGetSavedFilters,
  useDeleteFilter,
  useCreateFilter,
  useEditFilter,
  useEnableFilter,
  useNotifyFilterPopup,
  useFeatureFlags,
} from './hooks';

const getDataSource = () => {
  if (CURRENT_PRODUCT_MODE === 'APP') return DataSource.app;
  return DataSource.video;
};

export const OPEN_FILTER_MANAGEMENT_EVENT = 'open-filter-management';

export const FilterManagement = () => {
  const [dataSource, setDataSource] = useState<DataSource>(() => getDataSource());
  const [visible, setVisible] = useState(false);
  const { appEnabled, drilldownLimit, allowCreateFilter } = useFeatureFlags(dataSource);
  const userProfile = useSelector(getUserProfile) as User;
  const notifyActions = useLatest(useNotifyFilterPopup());

  const disabledDataSources = useMemo(() => {
    return appEnabled ? [] : [DataSource.app];
  }, [appEnabled]) as DataSource[];

  const disabledOperators = useMemo(
    () => ['regex', 'not_regex'] as ManagementProps['disabledOperators'],
    [],
  );

  const { mutateAsync: deleteFilter } = useDeleteFilter();
  const { mutateAsync: editFilter } = useEditFilter();
  const { mutateAsync: enableFilter } = useEnableFilter();
  const { mutateAsync: createFilter } = useCreateFilter();

  const {
    data: filterMeta,
    isLoading: filterMetaLoading,
    isError: filterMetaError,
    refetch: refetchFilterMeta,
  } = useFilterMeta(dataSource, {
    enabled: visible && !disabledDataSources.includes(dataSource),
  });

  const {
    data: savedFilters,
    isLoading: savedFiltersLoading,
    isError: savedFiltersError,
    refetch: refetchSavedFilters,
  } = useGetSavedFilters(dataSource, {
    enabled: visible && !disabledDataSources.includes(dataSource),
  });

  useEffect(() => {
    const handleOpenFilterManagement: any = () => {
      apptracker(OPEN_FILTER_MANAGEMENT_PANEL, {
        product: CURRENT_PRODUCT_MODE,
      });
      setVisible(true);
    };
    window.addEventListener(OPEN_FILTER_MANAGEMENT_EVENT, handleOpenFilterManagement);
    return () => {
      window.removeEventListener(OPEN_FILTER_MANAGEMENT_EVENT, handleOpenFilterManagement);
    };
  }, []);

  const handleDataSourceChange = useCallback((d: DataSource) => {
    setDataSource(d);
  }, []);

  const handleFilterMetaRetry = useCallback(() => {
    if (filterMetaError) {
      refetchFilterMeta();
    }
  }, [filterMetaError, refetchFilterMeta]);

  const handleSavedFiltersRetry = useCallback(() => {
    if (savedFiltersError) {
      refetchSavedFilters();
    }
  }, [savedFiltersError, refetchSavedFilters]);

  const handleClose = useCallback(() => {
    setVisible(false);
    setDataSource(getDataSource());
  }, []);

  const handleDeleteFilter = useCallback<NonNullable<ManagementProps['onDeleteFilter']>>(
    async (savedFilter, d) => {
      const result = await deleteFilter({
        id: savedFilter.fid,
        dataSource: d,
      });
      apptracker(DELETE_FILTER_ON_FILTER_MANAGEMENT, {
        filterId: savedFilter.fid,
        product: CURRENT_PRODUCT_MODE,
      });
      notifyActions.current.onDeleteNotify(savedFilter.fid, d);
      refetchSavedFilters();
      return result.data;
    },
    [deleteFilter, refetchSavedFilters, notifyActions],
  );

  const handleEditFilter = useCallback<NonNullable<ManagementProps['onEditFilter']>>(
    async (id, filter, d) => {
      const result = await editFilter({
        id,
        filter,
        dataSource: d,
      });
      notifyActions.current.onUpdateNotify(id, filter, d);
      refetchSavedFilters();
      return result.data;
    },
    [editFilter, refetchSavedFilters, notifyActions],
  );

  const handleEnableFilter = useCallback<NonNullable<ManagementProps['onEnableFilter']>>(
    async (id, d) => {
      const result = await enableFilter({
        id,
        dataSource: d,
      });
      refetchSavedFilters();
      return result.data;
    },
    [enableFilter, refetchSavedFilters],
  );

  const handleCloneFilter = useCallback<NonNullable<ManagementProps['onCloneFilter']>>(
    async (filter, d) => {
      const result = await createFilter({
        filter,
        dataSource: d,
      });
      const newSavedFilter = result.data.filter;
      if (newSavedFilter) {
        apptracker(CREATE_FILTER_ON_FILTER_MANAGEMENT, {
          filterId: newSavedFilter.fid,
          dataSource,
          product: CURRENT_PRODUCT_MODE,
        });
        notifyActions.current.onCreateNotify(d, newSavedFilter);
      }
      refetchSavedFilters();
      return result.data;
    },
    [createFilter, refetchSavedFilters, dataSource, notifyActions],
  );

  const handleCreateFilter = useCallback<NonNullable<ManagementProps['onCreateFilter']>>(
    async (filter, d) => {
      const result = await createFilter({
        filter,
        dataSource: d,
      });
      const newSavedFilter = result.data.filter;
      if (newSavedFilter) {
        apptracker(CREATE_FILTER_ON_FILTER_MANAGEMENT, {
          filterId: newSavedFilter.fid,
          dataSource,
          product: CURRENT_PRODUCT_MODE,
        });
        notifyActions.current.onCreateNotify(d, newSavedFilter);
      }
      refetchSavedFilters();
      return result.data;
    },
    [createFilter, refetchSavedFilters, dataSource, notifyActions],
  );

  return (
    <div id="filter-management-container" data-ifc-event={OPEN_FILTER_MANAGEMENT_EVENT}>
      <Management
        isFilterMetaLoading={filterMetaLoading}
        isSavedFiltersLoading={savedFiltersLoading}
        isFilterMetaError={filterMetaError}
        isSavedFiltersError={savedFiltersError}
        disableCreateFilter={!allowCreateFilter}
        disabledDataSources={disabledDataSources}
        disabledOperators={disabledOperators}
        drilldownLimit={drilldownLimit}
        visible={visible}
        user={userProfile}
        dataSource={dataSource}
        filterMeta={filterMeta!}
        savedFilters={savedFilters || []}
        onDataSourceChange={handleDataSourceChange}
        onClose={handleClose}
        onDeleteFilter={handleDeleteFilter}
        onEditFilter={handleEditFilter}
        onEnableFilter={handleEnableFilter}
        onCloneFilter={handleCloneFilter}
        onCreateFilter={handleCreateFilter}
        onFilterMetaRetry={handleFilterMetaRetry}
        onSavedFiltersRetry={handleSavedFiltersRetry}
      />
    </div>
  );
};
